// src/services/api.js
import axios from 'axios';

const getApiKey = () => {
  // 1. Из глобальной переменной (env.js)
  if (typeof window !== 'undefined' && window.env && window.env.VITE_API_KEY) {
    // console.log('🔑 Using API Key from window.env:', window.env.VITE_API_KEY);
    return window.env.VITE_API_KEY;
  }
  
  // 2. Из import.meta.env (Vite development)
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_KEY) {
    // console.log('🔑 Using API Key from import.meta.env:', import.meta.env.VITE_API_KEY);
    return import.meta.env.VITE_API_KEY;
  }
  
  // 3. Fallback - используем web_site ключ
  console.warn('⚠️ API Key not found in environment, using default');
  return 'web_site';
};

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location.hostname.includes('localhost')) {
    return 'http://localhost:8080/api/v1';
  }
  return 'https://anketa.miac-dnr.ru/api/v1';
};

const API_BASE_URL = getApiBaseUrl();
const API_KEY = getApiKey();

// console.log('🔧 API Configuration:', {
//   baseURL: API_BASE_URL,
//   hasApiKey: !!API_KEY,
//   apiKeyLength: API_KEY ? API_KEY.length : 0,
//   environment: typeof window !== 'undefined' ? window.location.hostname : 'server'
// });

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления API Key и токена к запросам
api.interceptors.request.use(
  (config) => {
    // Всегда добавляем API Key
    if (API_KEY) {
      config.headers['X-API-Key'] = API_KEY;
      // console.log('📤 Adding API Key to request:', config.url);
    } else {
      // console.error('❌ API Key is missing! Requests will fail.');
    }
    
    // Добавляем токен авторизации если есть
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // console.log('🚀 Request headers:', {
    //   url: config.url,
    //   hasApiKey: !!config.headers['X-API-Key'],
    //   apiKeyLength: config.headers['X-API-Key'] ? config.headers['X-API-Key'].length : 0,
    //   hasToken: !!token
    // });
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => {
    // console.log('✅ Request successful:', response.config.url);
    return response;
  },
  (error) => {
    // console.error('❌ Request failed:', {
    //   url: error.config?.url,
    //   status: error.response?.status,
    //   data: error.response?.data,
    // });
    
    // НЕ делаем редирект при ошибках 422 (валидация)
    if (error.response?.status === 422) {
      // console.log('⚠️ Validation error (no redirect):', error.response.data);
      // Просто пробрасываем ошибку для обработки в компоненте
      return Promise.reject(error);
    }
    
    // Редирект только при 401 (Unauthorized) - когда токен истек
    if (error.response?.status === 401) {
      // console.log('🔐 Unauthorized, redirecting to login');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Остальные ошибки просто пробрасываем
    return Promise.reject(error);
  }
);

export const authAPI = {
  // OAuth2 login (form-data)
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // JSON login
  loginJson: async (email, password) => {
    const response = await api.post('/auth/login-json', {
      email,
      password,
    });
    return response.data;
  },

  // Register
  register: async (name, email, password) => {
    const response = await api.post('/auth/register', {
      name,
      email,
      password,
    });
    return response.data;
  },

  // Get current user
  getCurrentUser: async () => {
    const response = await api.get('/users/me');
    return response.data;
  },

  // Telegram auth - получаем ссылку для авторизации
  getTelegramAuthUrl: async () => {
    const response = await api.get('/auth/telegram/auth-url');
    return response.data;
  },

  // Telegram auth - обработка callback
  telegramCallback: async (telegramData) => {
    const response = await api.post('/auth/telegram/callback', telegramData);
    return response.data;
  },

  // Verify Telegram auth
  verifyTelegramAuth: async (telegramData) => {
    // console.log('📨 Sending Widget Telegram data:', telegramData);
    
    const requestData = {
      id: parseInt(telegramData.id),
      first_name: telegramData.first_name,
      last_name: telegramData.last_name || '',
      username: telegramData.username || '',
      photo_url: telegramData.photo_url || '',
      auth_date: parseInt(telegramData.auth_date),
      hash: telegramData.hash
    };
    
    // console.log('📤 Final request data:', requestData);
    
    const response = await api.post('/auth/telegram/verify', requestData);
    return response.data;
  },
};

export const usersAPI = {
  // Получить список пользователей
  getUsers: async (params = {}) => {
    const response = await api.get('/users/', { params });
    return response.data;
  },

  // Получить пользователя по ID
  getUser: async (userId) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  // Обновить пользователя
  updateUser: async (userId, userData) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  // Удалить пользователя
  deleteUser: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  // ✅ СОЗДАТЬ ПОЛЬЗОВАТЕЛЯ (ТОЛЬКО ДЛЯ АДМИНОВ)
  createUser: async (userData) => {
    // console.log('📤 Creating user with data:', userData);
    const response = await api.post('/users/', userData);
    // console.log('✅ User created:', response.data);
    return response.data;
  },

  // Пакетная загрузка пользователей по ID
  getUsersBatch: async (userIds) => {
    if (!userIds || userIds.length === 0) return [];
    
    const response = await api.get('/users/', {
      params: { user_ids: userIds.join(',') }
    });
    return response.data;
  }
};

export const notificationsAPI = {
  // Получить уведомления пользователя
  getNotifications: async (params = {}) => {
    const response = await api.get('/notifications/', { params });
    return response.data;
  },

  // Получить статистику уведомлений
  getStats: async () => {
    const response = await api.get('/notifications/stats');
    return response.data;
  },

  // Отметить уведомление как прочитанное
  markAsRead: async (notificationId) => {
    const response = await api.patch(`/notifications/${notificationId}/read`);
    return response.data;
  },

  // Отметить все уведомления как прочитанные
  markAllAsRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },

  // Отправить уведомление (универсальный метод)
  sendUniversalNotification: async (notificationData) => {
    const response = await api.post('/notifications/send', notificationData);
    return response.data;
  },

  // Быстрая отправка уведомления
  quickNotify: async (user_id, title, message, send_telegram = true, send_websocket = true) => {
    const response = await api.post('/notifications/quick-notify', null, {
      params: {
        user_id,
        title,
        message,
        send_telegram,
        send_websocket
      }
    });
    return response.data;
  },

  // Массовая рассылка
  broadcast: async (broadcastData) => {
    const response = await api.post('/notifications/broadcast', broadcastData);
    return response.data;
  },

  // Рассылка администраторам
  broadcastToAdmins: async (title, message, send_telegram = true, send_websocket = true, priority = 'normal') => {
    const response = await api.post('/notifications/broadcast/admins', null, {
      params: {
        title,
        message,
        send_telegram,
        send_websocket,
        priority
      }
    });
    return response.data;
  },

  // Рассылка модераторам
  broadcastToModerators: async (title, message, send_telegram = true, send_websocket = true, priority = 'normal') => {
    const response = await api.post('/notifications/broadcast/moderators', null, {
      params: {
        title,
        message,
        send_telegram,
        send_websocket,
        priority
      }
    });
    return response.data;
  },

  // Рассылка всем пользователям
  broadcastToAll: async (title, message, send_telegram = true, send_websocket = true, priority = 'normal') => {
    const response = await api.post('/notifications/broadcast/all', null, {
      params: {
        title,
        message,
        send_telegram,
        send_websocket,
        priority
      }
    });
    return response.data;
  },

  // Получить статистику для рассылок
  getBroadcastStats: async () => {
    const response = await api.get('/notifications/broadcast/stats');
    return response.data;
  },

  // Тестовый endpoint
  testSend: async (user_id = 1, title = "Тестовое уведомление", message = "Это тестовое сообщение") => {
    const response = await api.post('/notifications/test-send', null, {
      params: {
        user_id,
        title,
        message
      }
    });
    return response.data;
  },

  // Тест WebSocket
  testWebSocket: async (user_id = 1, title = "Тест WebSocket", message = "Это тестовое уведомление через WebSocket") => {
    const response = await api.post('/notifications/test-websocket', null, {
      params: {
        user_id,
        title,
        message
      }
    });
    return response.data;
  },

  // Получить статус WebSocket соединений
  getWebSocketStatus: async () => {
    const response = await api.get('/notifications/websocket/status');
    return response.data;
  },

  // Очистка WebSocket соединений
  cleanupWebSocket: async () => {
    const response = await api.post('/notifications/websocket/cleanup');
    return response.data;
  }
};

export const tasksAPI = {
  // Получить список задач
  getTasks: async (params = {}) => {
    const response = await api.get('/tasks/', { params });
    return response.data;
  },

  // Получить задачу по ID
  getTask: async (taskId) => {
    const response = await api.get(`/tasks/${taskId}`);
    return response.data;
  },

  // Создать задачу
  createTask: async (taskData) => {
    // console.log('📤 Sending task creation request:', taskData);
    const response = await api.post('/tasks/', taskData);
    // console.log('✅ Task creation response:', response.data);
    
    // API возвращает объект с ID созданной задачи
    return response.data;
  },

  // Обновить задачу
  updateTask: async (taskId, taskData) => {
    const response = await api.put(`/tasks/${taskId}`, taskData);
    return response.data;
  },

  // Удалить задачу
  deleteTask: async (taskId) => {
    await api.delete(`/tasks/${taskId}`);
    return { success: true };
  },

  // Получить статистику задач
  getStats: async () => {
    const response = await api.get('/tasks/stats');
    return response.data;
  },

  // Завершить задачу
  completeTask: async (taskId, actualHours = null) => {
    const response = await api.post(`/tasks/${taskId}/complete`, null, {
      params: { actual_hours: actualHours }
    });
    return response.data;
  },

  // Назначить задачу
  assignTask: async (taskId, assigneeId) => {
    const response = await api.post(`/tasks/${taskId}/assign/${assigneeId}`);
    return response.data;
  },

  // Получить комментарии задачи - УБИРАЕМ, т.к. они приходят с задачей
  getComments: async (taskId) => {
    // console.log('⚠️ Comments endpoint not available, returning empty array');
    return [];
  },

  // Добавить комментарий - УБИРАЕМ, т.к. endpoint не работает
  addComment: async (taskId, text) => {
    // console.log('⚠️ Add comment endpoint not available');
    throw new Error('Добавление комментариев временно недоступно');
  },

  // Удалить комментарий - УБИРАЕМ
  deleteComment: async (taskId, commentId) => {
    // console.log('⚠️ Delete comment endpoint not available');
    throw new Error('Удаление комментариев временно недоступно');
  },

  // Добавить вложение (исправленная версия)
  addAttachment: async (taskId, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const config = {
      headers: {
        'X-API-Key': API_KEY,
      },
      onUploadProgress,
    };
    
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // console.log('📤 Uploading file config:', {
    //   taskId,
    //   filename: file.name,
    //   size: file.size,
    //   type: file.type,
    // });
    
    try {
      const response = await axios.post(`${API_BASE_URL}/tasks/${taskId}/attachments`, formData, config);
      // console.log('✅ File uploaded successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ File upload error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  // Скачать вложение
  downloadAttachment: async (taskId, attachmentId) => {
    const response = await api.get(`/tasks/${taskId}/attachments/${attachmentId}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

export const organizationsAPI = {
  // Получить список организаций
  getOrganizations: async (params = {}) => {
    const response = await api.get('/organizations/', { params });
    return response.data;
  },

  // Получить статистику организаций
  getStats: async () => {
    const response = await api.get('/organizations/stats');
    return response.data;
  },

  // Получить мои организации
  getMyOrganizations: async (params = {}) => {
    const response = await api.get('/organizations/my-organizations', { params });
    return response.data;
  },

  // Получить организацию по ID
  getOrganization: async (organizationId, params = {}) => {
    const response = await api.get(`/organizations/${organizationId}`, { params });
    return response.data;
  },

  // Создать организацию
  createOrganization: async (organizationData) => {
    const response = await api.post('/organizations/', organizationData);
    return response.data;
  },

  // Обновить организацию
  updateOrganization: async (organizationId, organizationData) => {
    const response = await api.put(`/organizations/${organizationId}`, organizationData);
    return response.data;
  },

  // Мягкое удаление организации
  softDeleteOrganization: async (organizationId, deleteData = {}) => {
    const response = await api.delete(`/organizations/${organizationId}`, { data: deleteData });
    return response.data;
  },

  // Переключить статус организации
  toggleOrganizationStatus: async (organizationId) => {
    const response = await api.patch(`/organizations/${organizationId}/toggle-status`);
    return response.data;
  },

  // Восстановить организацию
  restoreOrganization: async (organizationId, restoreData = {}) => {
    const response = await api.post(`/organizations/${organizationId}/restore`, restoreData);
    return response.data;
  },

  // Получить пользователей организации
  getOrganizationUsers: async (organizationId, params = {}) => {
    const response = await api.get(`/organizations/${organizationId}/users`, { params });
    return response.data;
  },

  // Назначить пользователя в организацию
  assignUserToOrganization: async (organizationId, userId) => {
    const response = await api.post(`/organizations/${organizationId}/users/${userId}/assign`);
    return response.data;
  },

  // Убрать пользователя из организации
  unassignUserFromOrganization: async (organizationId, userId) => {
    const response = await api.post(`/organizations/${organizationId}/users/${userId}/unassign`);
    return response.data;
  }
};

export const surveysAPI = {
  // Получить список анкет
  getSurveys: async (params = {}) => {
    const response = await api.get('/surveys/', { params });
    // Возвращаем как есть - бэкенд может вернуть { items, total }
    return response.data;
  },
  // Получить анкету по ID
  getSurvey: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}`);
    return response.data;
  },

  // Создать анкету
  createSurvey: async (surveyData) => {
    const response = await api.post('/surveys/', surveyData);
    return response.data;
  },

  // Обновить анкету
  updateSurvey: async (surveyId, surveyData) => {
    const response = await api.put(`/surveys/${surveyId}`, surveyData);
    return response.data;
  },

  // Архивировать анкету
  archiveSurvey: async (surveyId) => {
    const response = await api.delete(`/surveys/${surveyId}`);
    return response.data;
  },

  // Добавить вопрос
  addQuestion: async (surveyId, questionData) => {
    const response = await api.post(`/surveys/${surveyId}/questions`, questionData);
    return response.data;
  },

  // Удалить вопрос
  deleteQuestion: async (surveyId, questionId) => {
    const response = await api.delete(`/surveys/${surveyId}/questions/${questionId}`);
    return response.data;
  },

  // Получить аналитику анкеты
  getSurveyAnalytics: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/analytics`);
    return response.data;
  },

  // Получить отправки анкеты
  getSurveySubmissions: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/submissions`);
    return response.data;
  },

  // Шаблоны анкет
  getTemplates: async (params = {}) => {
    const response = await api.get('/surveys/templates', { params });
    return response.data;
  },

  // Создать шаблон
  createTemplate: async (templateData) => {
    const response = await api.post('/surveys/templates', templateData);
    return response.data;
  },

  // Публичные методы (без авторизации)
  getPublicSurvey: async (publicId) => {
    const response = await api.get(`/surveys/public/${publicId}`);
    return response.data;
  },

  // Отправить ответ на публичную анкету
  submitPublicSurvey: async (publicId, submissionData) => {
    const response = await api.post(`/surveys/public/${publicId}/submit`, submissionData);
    return response.data;
  },

  // Размножить шаблон для организаций
  duplicateTemplate: async (templateId, organizationIds) => {
    const response = await api.post(`/surveys/templates/${templateId}/duplicate`, { organization_ids: organizationIds });
    return response.data;
  },
  exportAnalytics: async (surveyId) => {
      const response = await api.get(`/surveys/${surveyId}/export`, {
          responseType: 'blob'  // Важно для скачивания файла
      });
      return response.data;
  }
};

export default api;